import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  PlusIcon, 
  SaveIcon, 
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  Loader2Icon,
  RefreshCwIcon,
  AlertCircleIcon
} from 'lucide-react';
import { InterviewQuestion } from '../types';
import { useInterviewStore } from '../store';
import { generateInterviewQuestions } from '../services/openai';
import { updateInterviewQuestions, supabase } from '../services/supabase';
import toast from 'react-hot-toast';

interface InterviewQuestionEditorProps {
  interviewId: string;
  onSave: () => void;
}

export function InterviewQuestionEditor({ interviewId, onSave }: InterviewQuestionEditorProps) {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState<InterviewQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const jobDescription = useInterviewStore((state) => state.jobDescription);
  const config = useInterviewStore((state) => state.interviewConfig);

  // Load existing questions from config
  useEffect(() => {
    const loadExistingQuestions = async () => {
      if (!interviewId) return;

      try {
        setLoading(true);
        setError(null);

        const { data: interview, error: fetchError } = await supabase
          .from('interviews')
          .select('config, status')
          .eq('id', interviewId)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (interview?.config?.questions?.length > 0) {
          setQuestions(interview.config.questions);
        } else if (jobDescription) {
          // Generate initial questions if none exist
          const generatedQuestions = await generateInterviewQuestions(jobDescription, config);
          setQuestions(generatedQuestions);
        }
      } catch (error) {
        console.error('Error loading questions:', error);
        setError(t('interview.questions.loadError'));
        toast.error(t('interview.questions.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadExistingQuestions();
  }, [interviewId, jobDescription, config, t]);

  const generateInitialQuestions = async () => {
    if (!jobDescription) {
      setError(t('interview.questions.jobDescriptionRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const generatedQuestions = await generateInterviewQuestions(jobDescription, config);
      setQuestions(generatedQuestions);
    } catch (error) {
      console.error('Error generating questions:', error);
      setError(t('interview.questions.generationError'));
      toast.error(t('interview.questions.generationError'));
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionChange = (index: number, value: string) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      question: value
    };
    setQuestions(updatedQuestions);
  };

  const handleTypeChange = (index: number, value: 'technical' | 'behavioral' | 'situational') => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      type: value
    };
    setQuestions(updatedQuestions);
  };

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: crypto.randomUUID(),
        question: '',
        type: 'behavioral',
        order: questions.length + 1,
        isCustom: true
      }
    ]);
  };

  const handleRemoveQuestion = (index: number) => {
    if (confirm(t('interview.questions.confirmDelete'))) {
      const updatedQuestions = questions.filter((_, i) => i !== index);
      setQuestions(updatedQuestions.map((q, i) => ({ ...q, order: i + 1 })));
    }
  };

  const handleMoveQuestion = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === questions.length - 1)
    ) {
      return;
    }

    const updatedQuestions = [...questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updatedQuestions[index], updatedQuestions[newIndex]] = [
      updatedQuestions[newIndex],
      updatedQuestions[index]
    ];

    setQuestions(updatedQuestions.map((q, i) => ({ ...q, order: i + 1 })));
  };

  const handleSave = async () => {
    if (questions.length === 0) {
      toast.error(t('interview.questions.noQuestions'));
      return;
    }

    if (questions.some(q => !q.question.trim())) {
      toast.error(t('interview.questions.emptyQuestions'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await updateInterviewQuestions(interviewId, questions);
      toast.success(t('interview.questions.saved'));
      onSave();
    } catch (error) {
      console.error('Error saving questions:', error);
      setError(t('interview.questions.saveError'));
      toast.error(t('interview.questions.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <Loader2Icon className="h-8 w-8 animate-spin text-indigo-600" />
          <p className="text-gray-600">{t('interview.questions.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          {t('interview.questions.title')}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={generateInitialQuestions}
            disabled={loading || saving}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCwIcon className="h-5 w-5" />
            {t('interview.questions.regenerate')}
          </button>
          <button
            onClick={handleSave}
            disabled={loading || saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2Icon className="h-5 w-5 animate-spin" />
                {t('interview.questions.saving')}
              </>
            ) : (
              <>
                <SaveIcon className="h-5 w-5" />
                {t('interview.questions.save')}
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center gap-2 text-red-700">
          <AlertCircleIcon className="h-5 w-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {questions.map((question, index) => (
          <div
            key={question.id}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
          >
            <div className="flex items-start gap-4">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-500">
                    {t('interview.questions.questionNumber', { number: index + 1 })}
                  </span>
                  <select
                    value={question.type}
                    onChange={(e) => handleTypeChange(index, e.target.value as any)}
                    className="text-sm border-gray-300 rounded-md"
                  >
                    <option value="technical">{t('interview.questions.types.technical')}</option>
                    <option value="behavioral">{t('interview.questions.types.behavioral')}</option>
                    <option value="situational">{t('interview.questions.types.situational')}</option>
                  </select>
                  {question.isCustom ? (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {t('interview.questions.customQuestion')}
                    </span>
                  ) : (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      {t('interview.questions.generatedQuestion')}
                    </span>
                  )}
                </div>
                <textarea
                  value={question.question}
                  onChange={(e) => handleQuestionChange(index, e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  rows={3}
                  placeholder={t('interview.questions.placeholder')}
                />
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleMoveQuestion(index, 'up')}
                  disabled={index === 0}
                  title={t('interview.questions.moveUp')}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <ArrowUpIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleMoveQuestion(index, 'down')}
                  disabled={index === questions.length - 1}
                  title={t('interview.questions.moveDown')}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <ArrowDownIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleRemoveQuestion(index)}
                  title={t('interview.questions.delete')}
                  className="p-1 text-red-400 hover:text-red-600"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleAddQuestion}
        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
      >
        <PlusIcon className="h-5 w-5" />
        {t('interview.questions.addQuestion')}
      </button>
    </div>
  );
}